/**
 * useCurrentUserPermissions — Resolves the full RolePermissions object for
 * the currently logged-in user by mapping their PlatformRole to the matching
 * mock role record.
 *
 * TODO (DB-BE-2): Once the real API is live this hook should fetch from
 *   GET /api/users/me/role
 * and return the permissions from the response instead of the mock lookup.
 */

import { useMemo } from 'react';
import { useCurrentUserRole } from '@/hooks/useCurrentUserRole';
import { MOCK_ROLES } from '@/lib/roles-mock-data';
import type { RolePermissions, SettingsPermissions } from '@/types/roles.types';

// ── Fallback — zero permissions (safest default for unknown roles) ─────────────

const NO_PERMISSIONS: RolePermissions = {
  projectVisibility: 'managed-allocated',
  projectActions: {
    create: false,
    edit: false,
    delete: false,
    changeStatus: false,
    assignKeyPeople: false,
  },
  projectTabs: {
    overview: 'View',
    financials: 'Hidden',
    billing: 'Hidden',
    salesBudget: 'Hidden',
    costBudget: 'Hidden',
    resourceBudget: 'Hidden',
    costCentres: 'Hidden',
    purchases: 'Hidden',
    time: 'View',
    expenses: 'View',
    team: 'View',
    resourcing: 'View',
    milestones: 'View',
    changeRequests: 'View',
    audit: 'Hidden',
    reports: 'Hidden',
  },
  reports: {
    executiveDashboard: false,
    pmDashboard: false,
    projectTeamDashboard: true,
    plReport: false,
    wipReport: false,
    utilisationReport: false,
    budgetVarianceReport: false,
    auditLogReport: false,
    projectTransactionsReport: false,
    projectStatusReport: false,
    projectForecastReport: false,
    profitabilityReport: false,
  },
  settings: {
    generalSettings: false,
    configureAuditTrail: false,
    projectSettings: false,
    integrations: false,
    rolesAndPermissions: false,
    templates: false,
    notifications: false,
  },
};

// ── Role-name to mock-role-id mapping ─────────────────────────────────────────

const PLATFORM_ROLE_TO_MOCK_ID: Record<string, string> = {
  administrator: 'role-administrator',
  executive: 'role-executive',
  project_manager: 'role-project-manager',
  project_team: 'role-project-team',
};

// ── Hook ──────────────────────────────────────────────────────────────────────

export interface CurrentUserPermissions {
  permissions: RolePermissions;
  settings: SettingsPermissions;
  /** True when sourced from mock data rather than a real API */
  isMock: boolean;
}

export function useCurrentUserPermissions(): CurrentUserPermissions {
  const { role, isMock } = useCurrentUserRole();

  return useMemo(() => {
    const mockId = PLATFORM_ROLE_TO_MOCK_ID[role];
    const matchedRole = mockId
      ? MOCK_ROLES.find((r) => r.id === mockId)
      : undefined;

    const permissions = matchedRole?.permissions ?? NO_PERMISSIONS;

    return {
      permissions,
      settings: permissions.settings,
      isMock,
    };
  }, [role, isMock]);
}
